import type { DomainOffersEntry } from '@/pages/useCalculateOffers';
import type { CalculateOffersPersonResult } from '@/services/inventory-api.service';

function normalizeAgeCategory(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function getDomainAgeKey(domainId: number, ageCategory: string | undefined): string {
  return `${String(domainId)}|${normalizeAgeCategory(ageCategory)}`;
}

function resolvePersonAgeCategory(person: CalculateOffersPersonResult): string {
  const items = Array.isArray(person.items) ? person.items : [];
  const passItem = items.find(
    (item) => item.type === 'pass' && (item.customer_type ?? '').trim().length > 0
  );
  if (passItem?.customer_type) return passItem.customer_type;
  return items.find((item) => (item.customer_type ?? '').trim().length > 0)?.customer_type ?? '';
}

export function buildSkicardAddonQueuesByDomainAge(
  dataByDomain: DomainOffersEntry[]
): Map<string, number[]> {
  const result = new Map<string, number[]>();

  for (const { domain, offers } of dataByDomain) {
    const people = Array.isArray(offers.people) ? offers.people : [];

    for (const person of people) {
      const personItems = Array.isArray(person.items) ? person.items : [];
      const personAge = resolvePersonAgeCategory(person);
      const skicardTotal = personItems.reduce((sum, personItem) => {
        if (personItem.type !== 'skicard') return sum;
        return sum + Math.max(0, Math.round(personItem.price));
      }, 0);

      if (skicardTotal <= 0) continue;

      const key = getDomainAgeKey(domain.pk, personAge);
      const queue = result.get(key) ?? [];
      queue.push(skicardTotal);
      result.set(key, queue);
    }
  }

  return result;
}

export function takeSkicardAddonPrice(
  queuesByDomainAge: Map<string, number[]>,
  domainId: number,
  ageCategory: string | undefined
): number {
  const exactKey = getDomainAgeKey(domainId, ageCategory);
  const exactQueue = queuesByDomainAge.get(exactKey) ?? [];

  if (exactQueue.length > 0) {
    const taken = exactQueue.shift();
    if (typeof taken === 'number' && Number.isFinite(taken)) {
      queuesByDomainAge.set(exactKey, exactQueue);
      return Math.max(0, Math.round(taken));
    }
  }

  const domainPrefix = `${String(domainId)}|`;
  for (const [key, queue] of queuesByDomainAge.entries()) {
    if (!key.startsWith(domainPrefix)) continue;
    if (queue.length === 0) continue;

    const taken = queue.shift();
    if (typeof taken === 'number' && Number.isFinite(taken)) {
      queuesByDomainAge.set(key, queue);
      return Math.max(0, Math.round(taken));
    }
  }

  return 0;
}
